import { fetchAllChats } from "../sync";
import { fetchAPI } from "../client";
import { fetchInstanceDetails } from "../instance";

/**
 * Busca a foto de perfil de um contato via Uazapi API.
 * Endpoint: POST chat/fetchProfilePictureUrl/{instanceName}
 *
 * NOTA: Usa apenas 1 tentativa (sem retry) pois muitos contatos
 * não terão foto disponível. Erros 404/405 são silenciosos.
 */
export async function fetchProfilePicture(
  instanceName: string,
  phone: string,
): Promise<string | null> {
  try {
    const data = await fetchAPI<any>(
      `chat/fetchProfilePictureUrl/${instanceName}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", instance: instanceName },
        body: JSON.stringify({ number: phone }),
      },
      1,
      500,
    );
    return (
      data?.profilePictureUrl ||
      data?.url ||
      data?.profilePicUrl ||
      data?.picture ||
      null
    );
  } catch {
    return null;
  }
}

export const fetchChats = async (instanceName: string) => {
  const ownerJid = await getOwnerJid(instanceName);
  return fetchAllChats(instanceName, 50, 100, ownerJid);
};

/**
 * Busca mensagens não lidas de uma instância.
 * Usa message/find com limite de 50 e filtra as não lidas.
 */
export const fetchUnreadMessages = async (instanceName: string) => {
  try {
    const data = await fetchAPI<any>(
      "message/find",
      {
        method: "POST",
        headers: { "Content-Type": "application/json", instance: instanceName },
        body: JSON.stringify({ limit: 50, offset: 0 }),
      },
      1,
      500,
    );

    let messages: any[] = [];
    if (data?.messages && Array.isArray(data.messages))
      messages = data.messages;
    else if (data?.chats && Array.isArray(data.chats)) messages = data.chats;
    else if (Array.isArray(data)) messages = data;

    return messages.filter(
      (msg: any) =>
        !msg.key?.fromMe && msg.status !== "READ" && msg.status !== "PLAYED",
    );
  } catch {
    return [];
  }
};

export const fetchContacts = async (instanceName: string) => {
  try {
    const data = await fetchAPI<any>("contact/find", {
      method: "POST",
      headers: { "Content-Type": "application/json", instance: instanceName },
      body: JSON.stringify({}),
    });
    if (Array.isArray(data)) return data;
    if (data?.contacts) return data.contacts;
    if (data?.chats) return data.chats;
    return [];
  } catch {
    return [];
  }
};

export function extractChatImageUrl(
  chat: Record<string, unknown> | null | undefined,
): string | null {
  if (!chat) return null;

  const picture = chat.picture;
  const pictureUrl = typeof picture === "string"
    ? picture
    : typeof picture === "object" && picture !== null && "url" in picture
      ? String((picture as Record<string, unknown>).url || "")
      : "";

  const candidates = [
    chat.image,
    chat.imagePreview,
    chat.profilePicUrl,
    chat.profilePictureUrl,
    pictureUrl,
  ];

  for (const candidate of candidates) {
    const url = typeof candidate === "string" ? candidate.trim() : "";
    if (!url || url.includes("ui-avatars.com") || url.includes("NO+URL")) continue;
    return url;
  }

  return null;
}

/**
 * Busca detalhes completos do chat via POST /chat/details (Uazapi).
 * @see https://docs.uazapi.com/endpoint/post/chat~details
 */
export async function fetchChatDetails(
  instanceName: string,
  phone: string,
  preview = true,
): Promise<Record<string, unknown> | null> {
  try {
    const cleanNumber = phone.replace(/\D/g, "");
    const data = await fetchAPI<any>("chat/details", {
      method: "POST",
      headers: { "Content-Type": "application/json", instance: instanceName },
      body: JSON.stringify({ number: cleanNumber, preview }),
    }, 1, 500);
    const chat = { ...(data?.chat || data) } as Record<string, unknown>;
    if (!chat.image && data?.image) chat.image = data.image;
    if (!chat.imagePreview && data?.imagePreview) chat.imagePreview = data.imagePreview;
    return chat;
  } catch {
    return null;
  }
}

function isLikelyPhoneName(name: string | undefined, phone: string): boolean {
  if (!name?.trim()) return true;
  const digitsOnly = name.replace(/\D/g, "");
  const phoneDigits = phone.replace(/\D/g, "");
  return digitsOnly === phoneDigits || /^\+?[\d\s().-]+$/.test(name.trim());
}

function isUsableContactName(name: string | undefined, phone?: string): boolean {
  if (!name?.trim()) return false;
  const trimmed = name.trim();
  if (trimmed.length < 2) return false;
  if (/^[\s.\-_?*#@!~]+$/.test(trimmed)) return false;
  if (phone && isLikelyPhoneName(trimmed, phone)) return false;
  return true;
}

export function resolveContactNameFromChat(
  chat: Record<string, unknown> | null | undefined,
  fallback: string,
  phone: string,
): string {
  const candidates = [
    chat?.wa_contactName,
    chat?.wa_name,
    chat?.lead_fullName,
    chat?.lead_name,
    chat?.name,
    fallback,
  ];
  for (const candidate of candidates) {
    const value = typeof candidate === "string" ? candidate.trim() : "";
    if (isUsableContactName(value, phone)) return value;
  }
  return isUsableContactName(fallback, phone) ? fallback : phone;
}

export { isUsableContactName };

export async function getOwnerJid(instanceName: string): Promise<string> {
  try {
    const details = await fetchInstanceDetails(instanceName);
    if (details?.number) {
      const num = String(details.number).replace(/\D/g, "");
      return num.includes("@") ? num : `${num}@s.whatsapp.net`;
    }
  } catch {
    /* fallback */
  }
  return "";
}
